import { useCallback, useEffect, useRef, useState } from 'react';
import { useSystemConfigStore } from 'store/systemConfigStore';
import { AuthService } from 'api/services/authService';

export type SessionPhase = 'active' | 'warn' | 'expired';

interface SessionTimer {
  phase: SessionPhase;
  remainingSeconds: number;
  resetTimer: () => void;
}

/**
 * Cada cuántos segundos se hace un ping al servidor para detectar cierres forzados.
 * 10 min — red de seguridad si el WebSocket force-logout (useForceLogout) cae.
 * El WS es el canal autoritativo (sub-segundo); este heartbeat solo cubre el
 * escenario degradado de WS desconectado. Ver fix_2026-05-27_heartbeat-sesion-tuning.
 */
const HEARTBEAT_INTERVAL_S = 600;

export function useSessionTimer(): SessionTimer {
  const sessionTimeout       = useSystemConfigStore((s) => s.sessionTimeout);
  const sessionWarningBefore = useSystemConfigStore((s) => s.sessionWarningBefore);

  const startRef     = useRef<number>(Date.now());
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [phase, setPhase]                = useState<SessionPhase>('active');
  const [remainingSeconds, setRemaining] = useState<number>(sessionTimeout * 60);

  const clearTimer = () => {
    if (intervalRef.current)  clearInterval(intervalRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
  };

  const resetTimer = useCallback(() => {
    startRef.current = Date.now();
    setPhase('active');
    setRemaining(sessionTimeout * 60);
  }, [sessionTimeout]);

  useEffect(() => {
    const timeoutMs = sessionTimeout * 60 * 1000;
    const warnMs    = Math.max((sessionTimeout - sessionWarningBefore) * 60 * 1000, 0);

    startRef.current = Date.now();
    setPhase('active');
    setRemaining(sessionTimeout * 60);

    clearTimer();

    // Countdown cada segundo
    intervalRef.current = setInterval(() => {
      const elapsed   = Date.now() - startRef.current;
      const remaining = Math.max(Math.ceil((timeoutMs - elapsed) / 1000), 0);

      setRemaining(remaining);

      if (elapsed >= timeoutMs) {
        setPhase('expired');
        clearTimer();
      } else if (elapsed >= warnMs) {
        setPhase('warn');
      }
    }, 1000);

    // Heartbeat: ping al servidor para detectar cierres forzados por el admin.
    // Si el servidor responde 401 (sesión force-cerrada), el interceptor de
    // client.ts llama clearAuth() y redirige al login automáticamente.
    heartbeatRef.current = setInterval(() => {
      AuthService.check().catch(() => {});
    }, HEARTBEAT_INTERVAL_S * 1000);

    return clearTimer;
  }, [sessionTimeout, sessionWarningBefore]);

  return { phase, remainingSeconds, resetTimer };
}
